/**
 * NeoCore · Mantenimiento de encabezado/pie de reportes
 *
 * Config global usada por todos los reportes contables al exportar.
 * Editable solo por admin. Task #143.
 */

import { useEffect, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { invalidateConfigReporte, type ConfigReporte } from '@/lib/useConfigReporte'

export function NeoCoreConfigReportePage() {
  const [cfg, setCfg] = useState<ConfigReporte | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [ok, setOk] = useState<string | null>(null)
  const [err, setErr] = useState<string | null>(null)

  useEffect(() => {
    supabase.from('config_reporte').select('*').eq('id', 1).maybeSingle()
      .then(({ data }) => { setCfg(data as ConfigReporte); setLoading(false) })
  }, [])

  const upd = (k: keyof ConfigReporte, v: string) => setCfg((c) => c ? { ...c, [k]: v } : c)

  const guardar = async () => {
    if (!cfg) return
    setSaving(true); setErr(null); setOk(null)
    const { error } = await supabase.from('config_reporte').update({
      ...cfg,
      updated_at: new Date().toISOString(),
    }).eq('id', 1)
    setSaving(false)
    if (error) setErr(error.message)
    else { invalidateConfigReporte(); setOk('Guardado · próximos reportes usan estos datos') }
  }

  if (loading || !cfg) return <div style={{ padding: 40, textAlign: 'center' }}>Cargando…</div>

  return (
    <div style={{ padding: 20, maxWidth: 900, margin: '0 auto' }}>
      <h1 style={{ margin: 0, fontSize: 22, fontWeight: 500 }}>Encabezado y pie de reportes</h1>
      <p style={{ margin: '4px 0 20px', color: 'var(--muted-foreground)', fontSize: 13 }}>
        Datos institucionales que aparecen en todos los reportes contables al imprimir o exportar.
      </p>

      <div style={{ display: 'grid', gap: 20 }}>
        <Section title="Datos legales">
          <Row label="Razón social" value={cfg.razon_social} onChange={(v) => upd('razon_social', v)} />
          <Row label="Nombre comercial" value={cfg.nombre_comercial ?? ''} onChange={(v) => upd('nombre_comercial', v)} />
          <Row label="NIT" value={cfg.nit} onChange={(v) => upd('nit', v)} />
          <Row label="Dirección fiscal" value={cfg.direccion_fiscal} onChange={(v) => upd('direccion_fiscal', v)} multiline />
          <Row label="Teléfono" value={cfg.telefono ?? ''} onChange={(v) => upd('telefono', v)} />
          <Row label="Email" value={cfg.email ?? ''} onChange={(v) => upd('email', v)} />
        </Section>

        <Section title="Firmas responsables">
          <Row label="Perito contador (nombre)" value={cfg.perito_contador_nombre ?? ''} onChange={(v) => upd('perito_contador_nombre', v)} />
          <Row label="Perito contador (NIT)" value={cfg.perito_contador_nit ?? ''} onChange={(v) => upd('perito_contador_nit', v)} />
          <Row label="Representante legal (nombre)" value={cfg.representante_legal_nombre ?? ''} onChange={(v) => upd('representante_legal_nombre', v)} />
        </Section>

        <Section title="Autorizaciones SAT / IGC">
          <Row label="Resolución IGC (habilitación)" value={cfg.resolucion_igc ?? ''} onChange={(v) => upd('resolucion_igc', v)} multiline />
          <Row label="Resolución SAT (impresos)" value={cfg.resolucion_sat ?? ''} onChange={(v) => upd('resolucion_sat', v)} multiline />
          <Row label="Pie legal adicional (opcional)" value={cfg.pie_legal ?? ''} onChange={(v) => upd('pie_legal', v)} multiline />
        </Section>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', alignItems: 'center' }}>
          {ok && <span style={{ color: '#047857', fontSize: 13 }}>{ok}</span>}
          {err && <span style={{ color: '#b91c1c', fontSize: 13 }}>{err}</span>}
          <button onClick={guardar} disabled={saving} style={{
            padding: '10px 20px', border: 'none', borderRadius: 8,
            background: 'var(--brand-600, #FF5B04)', color: '#fff',
            fontSize: 13, fontWeight: 500, cursor: 'pointer',
          }}>
            {saving ? 'Guardando…' : 'Guardar cambios'}
          </button>
        </div>
      </div>
    </div>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: 'var(--surface, #fff)', border: '1px solid var(--border)', borderRadius: 12, padding: 20 }}>
      <div style={{ fontSize: 11, color: 'var(--muted-foreground)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 500, marginBottom: 14 }}>
        {title}
      </div>
      <div style={{ display: 'grid', gap: 12 }}>{children}</div>
    </div>
  )
}

function Row({ label, value, onChange, multiline }: { label: string; value: string; onChange: (v: string) => void; multiline?: boolean }) {
  return (
    <label style={{ display: 'grid', gap: 4 }}>
      <span style={{ fontSize: 12, color: 'var(--muted-foreground)' }}>{label}</span>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} rows={2}
          style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }} />
      ) : (
        <input value={value} onChange={(e) => onChange(e.target.value)}
          style={{ padding: 8, border: '1px solid var(--border)', borderRadius: 6, fontSize: 13, fontFamily: 'inherit' }} />
      )}
    </label>
  )
}
